import React from "react";
import { Plus, Trash2, AlertCircle } from "lucide-react";
import { motion } from "framer-motion";
import DocumentUpload from "../../../common_components/DocumentUpload";
import { calculateExpectedFee } from "../validators/validators";

interface DynamicFieldProps {
  field: any;
  value: any;
  onChange: (id: string, val: any) => void;
  error?: any;
  applicationId: string;
  disabled?: boolean;
  folder?: string;
  recruitment?: any;
  sectionData?: any;
}

export default function DynamicField({
  field,
  value,
  onChange,
  error,
  applicationId,
  disabled = false,
  folder = "applications",
  recruitment,
  sectionData,
}: DynamicFieldProps) {
  const { id, label, type, required, options, validations } = field;

  const isDepartmentField = id === "department";
  const isPostField = id === "postName";
  const isCategoryOfPostField = id === "categoryOfPost";

  let resolvedType = type;
  let resolvedOptions = options;

  if (isDepartmentField && recruitment?.departments) {
    resolvedType = "select";
    const candidateType = sectionData?.candidateType;
    const categoryOfCandidate = sectionData?.categoryOfCandidate;

    resolvedOptions = recruitment.departments
      .filter((d: any) => {
        // 1. Candidate Type Check
        if (candidateType) {
          const cType = String(candidateType).toUpperCase().replace("_", "-");
          const dType = String(d.candidateType || "").toUpperCase().replace("_", "-");
          if (dType === "MEDICAL" && cType === "NON-MEDICAL") {
            return false;
          }
        }
        // 2. Category seat matrix check: department must have at least one eligible vacancy
        if (categoryOfCandidate) {
          const hasEligibleVacancy = (d.vacancies || []).some((v: any) => {
            const cats = v.categories || { UR: 0, OBC: 0, SC: 0, ST: 0, EWS: 0 };
            const getVal = (catKey: string) => Number(cats[catKey]) || 0;
            if (categoryOfCandidate === "UR") return getVal("UR") > 0;
            if (categoryOfCandidate === "OBC") return getVal("OBC") > 0 || getVal("UR") > 0;
            if (categoryOfCandidate === "SC") return getVal("SC") > 0 || getVal("UR") > 0;
            if (categoryOfCandidate === "ST") return getVal("ST") > 0 || getVal("UR") > 0;
            if (categoryOfCandidate === "EWS") return getVal("EWS") > 0 || getVal("UR") > 0;
            return true;
          });
          if (!hasEligibleVacancy) return false;
        }
        return true;
      })
      .map((d: any) => d.name);
  } else if (isPostField && recruitment?.departments) {
    resolvedType = "select";
    const selectedDept = sectionData?.department;
    const deptObj = recruitment.departments.find((d: any) => d.name === selectedDept);

    const candidateType = sectionData?.candidateType;
    const categoryOfCandidate = sectionData?.categoryOfCandidate;

    if (deptObj) {
      resolvedOptions = (deptObj.vacancies || [])
        .filter((v: any) => {
          // 1. Candidate Type Check
          if (candidateType) {
            const cType = String(candidateType).toUpperCase().replace("_", "-");
            const dType = String(deptObj.candidateType || "").toUpperCase().replace("_", "-");
            if (dType === "MEDICAL" && cType === "NON-MEDICAL") {
              return false;
            }
          }
          // 2. Category check (vacancy level)
          if (categoryOfCandidate) {
            const cats = v.categories || { UR: 0, OBC: 0, SC: 0, ST: 0, EWS: 0 };
            const getVal = (catKey: string) => Number(cats[catKey]) || 0;
            let eligible = false;
            if (categoryOfCandidate === "UR") {
              eligible = getVal("UR") > 0;
            } else if (categoryOfCandidate === "OBC") {
              eligible = getVal("OBC") > 0 || getVal("UR") > 0;
            } else if (categoryOfCandidate === "SC") {
              eligible = getVal("SC") > 0 || getVal("UR") > 0;
            } else if (categoryOfCandidate === "ST") {
              eligible = getVal("ST") > 0 || getVal("UR") > 0;
            } else if (categoryOfCandidate === "EWS") {
              eligible = getVal("EWS") > 0 || getVal("UR") > 0;
            }
            if (!eligible) return false;
          }
          return true;
        })
        .map((v: any) => v.post);
    } else {
      resolvedOptions = [];
    }
  } else if (isCategoryOfPostField && recruitment?.departments) {
    resolvedType = "select";
    const selectedDept = sectionData?.department;
    const selectedPost = sectionData?.postName;
    const categoryOfCandidate = sectionData?.categoryOfCandidate;

    if (!categoryOfCandidate) {
      resolvedOptions = [];
    } else {
      const deptObj = recruitment.departments.find((d: any) => d.name === selectedDept);
      const vacancy = deptObj?.vacancies?.find((v: any) => v.post === selectedPost);

      let baseOptions = options || ["UR", "OBC", "SC", "ST", "EWS"];

      if (vacancy) {
        const cats = vacancy.categories || { UR: 0, OBC: 0, SC: 0, ST: 0, EWS: 0 };
        baseOptions = baseOptions.filter((opt: any) => (Number(cats[opt]) || 0) > 0);
      }

      const allowedRules: Record<string, string[]> = {
        UR: ["UR"],
        OBC: ["UR", "OBC"],
        SC: ["UR", "SC"],
        ST: ["UR", "ST"],
        EWS: ["UR", "EWS"],
      };
      const allowed = allowedRules[categoryOfCandidate] || [];
      baseOptions = baseOptions.filter((opt: any) => allowed.includes(opt));

      resolvedOptions = baseOptions;
    }
  }

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    onChange(id, e.target.value);
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(id, e.target.checked);
  };

  // --- Address field handlers ---
  const handleAddressChange = (subField: string, subVal: any) => {
    const currentAddress = value || { addressLine: "", city: "", state: "", pincode: "" };
    onChange(id, { ...currentAddress, [subField]: subVal });
  };

  // --- Repeater field handlers ---
  const handleRepeaterAdd = () => {
    const list = Array.isArray(value) ? [...value] : [];
    const newEntry: Record<string, any> = {};
    field.fields.forEach((sf: any) => {
      newEntry[sf.id] = sf.type === "checkbox" ? false : "";
    });
    list.push(newEntry);
    onChange(id, list);
  };

  const handleRepeaterRemove = (index: number) => {
    const list = Array.isArray(value) ? [...value] : [];
    list.splice(index, 1);
    onChange(id, list);
  };

  const handleRepeaterChange = (index: number, subFieldId: string, subVal: any) => {
    const list = Array.isArray(value) ? [...value] : [];
    list[index] = { ...list[index], [subFieldId]: subVal };
    onChange(id, list);
  };

  // Helper to retrieve field errors for sub-addresses or repeater indexes
  const getSubError = (subPath: string) => {
    if (typeof error === "object" && error !== null) {
      return error[subPath];
    }
    return null;
  };

  const baseInputClass = "w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed transition-all";
  const errorInputClass = "border-red-500 focus:ring-red-500";

  switch (resolvedType) {
    case "text":
    case "email":
    case "phone":
      return (
        <div className="space-y-1">
          <label className="block text-sm font-medium text-gray-700">
            {label} {required && <span className="text-red-500">*</span>}
          </label>
          <input
            type={resolvedType === "phone" ? "tel" : "text"}
            value={value || ""}
            onChange={handleTextChange}
            disabled={disabled}
            className={`${baseInputClass} ${error ? errorInputClass : ""}`}
          />
          {error && typeof error === "string" && (
            <p className="text-xs text-red-500 flex items-center gap-1">
              <AlertCircle className="w-3.5 h-3.5" /> {error}
            </p>
          )}
        </div>
      );

    case "number":
      return (
        <div className="space-y-1">
          <label className="block text-sm font-medium text-gray-700">
            {label} {required && <span className="text-red-500">*</span>}
          </label>
          <input
            type="number"
            value={value !== undefined && value !== null ? value : ""}
            onChange={(e) => {
              const val = e.target.value === "" ? "" : Number(e.target.value);
              onChange(id, val);
            }}
            disabled={disabled}
            min={validations?.min}
            max={validations?.max}
            className={`${baseInputClass} ${error ? errorInputClass : ""}`}
            placeholder={`Enter ${label.toLowerCase()}`}
          />
          {error && typeof error === "string" && (
            <p className="text-xs text-red-500 flex items-center gap-1">
              <AlertCircle className="w-3.5 h-3.5" /> {error}
            </p>
          )}
        </div>
      );

    case "textarea":
      return (
        <div className="space-y-1">
          <label className="block text-sm font-medium text-gray-700">
            {label} {required && <span className="text-red-500">*</span>}
          </label>
          <textarea
            value={value || ""}
            onChange={handleTextChange}
            disabled={disabled}
            rows={3}
            className={`${baseInputClass} ${error ? errorInputClass : ""}`}
            placeholder={`Enter ${label.toLowerCase()}`}
          />
          {error && typeof error === "string" && (
            <p className="text-xs text-red-500 flex items-center gap-1">
              <AlertCircle className="w-3.5 h-3.5" /> {error}
            </p>
          )}
        </div>
      );

    case "date":
      return (
        <div className="space-y-1">
          <label className="block text-sm font-medium text-gray-700">
            {label} {required && <span className="text-red-500">*</span>}
          </label>
          <div className="relative">
            <input
              type="date"
              value={value ? value.substring(0, 10) : ""}
              onChange={handleTextChange}
              disabled={disabled}
              className={`${baseInputClass} ${error ? errorInputClass : ""}`}
            />
          </div>
          {error && typeof error === "string" && (
            <p className="text-xs text-red-500 flex items-center gap-1">
              <AlertCircle className="w-3.5 h-3.5" /> {error}
            </p>
          )}
        </div>
      );

    case "select":
      const isDisabledSelect =
        disabled ||
        (isDepartmentField && !sectionData?.categoryOfCandidate) ||
        (isPostField && (!sectionData?.categoryOfCandidate || !sectionData?.department)) ||
        (isCategoryOfPostField && (!sectionData?.categoryOfCandidate || !sectionData?.department || !sectionData?.postName));

      const selectPlaceholder =
        isDepartmentField && !sectionData?.categoryOfCandidate
          ? "Please select Category of Candidate first"
          : isPostField && !sectionData?.categoryOfCandidate
            ? "Please select Category of Candidate first"
            : isPostField && !sectionData?.department
              ? "Please select Department first"
              : isCategoryOfPostField && !sectionData?.categoryOfCandidate
                ? "Please select Category of Candidate first"
                : isCategoryOfPostField && (!sectionData?.department || !sectionData?.postName)
                  ? "Please select Department and Post Name first"
                  : `Select ${label}`;

      return (
        <div className="space-y-1">
          <label className="block text-sm font-medium text-gray-700">
            {label} {required && <span className="text-red-500">*</span>}
          </label>
          <select
            value={value || ""}
            onChange={handleTextChange}
            disabled={isDisabledSelect}
            className={`${baseInputClass} ${error ? errorInputClass : ""}`}
          >
            <option value="">{selectPlaceholder}</option>
            {resolvedOptions &&
              resolvedOptions.map((opt: string) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
          </select>
          {error && typeof error === "string" && (
            <p className="text-xs text-red-500 flex items-center gap-1">
              <AlertCircle className="w-3.5 h-3.5" /> {error}
            </p>
          )}
        </div>
      );

    case "checkbox":
      return (
        <div className="flex items-center gap-2 pt-2">
          <input
            type="checkbox"
            id={id}
            checked={!!value}
            onChange={handleCheckboxChange}
            disabled={disabled}
            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-60"
          />
          <label htmlFor={id} className="text-sm font-medium text-gray-700 cursor-pointer">
            {label} {required && <span className="text-red-500">*</span>}
          </label>
          {error && typeof error === "string" && (
            <p className="text-xs text-red-500 ml-2 flex items-center gap-1">
              <AlertCircle className="w-3.5 h-3.5" /> {error}
            </p>
          )}
        </div>
      );

    case "address":
      const addrValue = value || { addressLine: "", city: "", state: "", pincode: "" };
      return (
        <div className="p-4 bg-gray-50 rounded-xl border border-gray-200 space-y-4">
          <span className="block text-sm font-semibold text-gray-800">
            {label} {required && <span className="text-red-500">*</span>}
          </span>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-3 space-y-1">
              <label className="block text-xs font-medium text-gray-500">Address Line</label>
              <input
                type="text"
                value={addrValue.addressLine || ""}
                onChange={(e) => handleAddressChange("addressLine", e.target.value)}
                disabled={disabled}
                placeholder="House No, Street, Landmark"
                className={`${baseInputClass} ${getSubError("addressLine") ? errorInputClass : ""}`}
              />
              {getSubError("addressLine") && (
                <p className="text-xs text-red-500 flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5" /> {getSubError("addressLine")}
                </p>
              )}
            </div>
            <div className="space-y-1">
              <label className="block text-xs font-medium text-gray-500">City</label>
              <input
                type="text"
                value={addrValue.city || ""}
                onChange={(e) => handleAddressChange("city", e.target.value)}
                disabled={disabled}
                placeholder="City"
                className={`${baseInputClass} ${getSubError("city") ? errorInputClass : ""}`}
              />
              {getSubError("city") && (
                <p className="text-xs text-red-500 flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5" /> {getSubError("city")}
                </p>
              )}
            </div>
            <div className="space-y-1">
              <label className="block text-xs font-medium text-gray-500">State</label>
              <input
                type="text"
                value={addrValue.state || ""}
                onChange={(e) => handleAddressChange("state", e.target.value)}
                disabled={disabled}
                placeholder="State"
                className={`${baseInputClass} ${getSubError("state") ? errorInputClass : ""}`}
              />
              {getSubError("state") && (
                <p className="text-xs text-red-500 flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5" /> {getSubError("state")}
                </p>
              )}
            </div>
            <div className="space-y-1">
              <label className="block text-xs font-medium text-gray-500">Pincode</label>
              <input
                type="text"
                value={addrValue.pincode || ""}
                onChange={(e) => handleAddressChange("pincode", e.target.value)}
                disabled={disabled}
                placeholder="6 Digits"
                maxLength={6}
                className={`${baseInputClass} ${getSubError("pincode") ? errorInputClass : ""}`}
              />
              {getSubError("pincode") && (
                <p className="text-xs text-red-500 flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5" /> {getSubError("pincode")}
                </p>
              )}
            </div>
          </div>
          {error && typeof error === "string" && (
            <p className="text-xs text-red-500 flex items-center gap-1">
              <AlertCircle className="w-3.5 h-3.5" /> {error}
            </p>
          )}
        </div>
      );

    case "file":
    case "image":
      let isDocRequired = required;
      let docLabel = label;
      if (id === "paymentCert" && recruitment) {
        const fee = calculateExpectedFee({ personalInfo: sectionData }, recruitment);
        if (fee === 0) {
          isDocRequired = false;
          docLabel = `${label} (Optional - Fee Exempted)`;
        }
      }
      return (
        <div className="space-y-1">
          <DocumentUpload
            applicationId={applicationId}
            docType={id}
            label={docLabel}
            required={isDocRequired}
            existingDoc={value}
            onUploadComplete={(type, docData) => onChange(id, docData)}
            folder={folder}
            disabled={disabled}
          />
          {error && typeof error === "string" && (
            <p className="text-xs text-red-500 flex items-center gap-1 mt-1">
              <AlertCircle className="w-3.5 h-3.5" /> {error}
            </p>
          )}
        </div>
      );

    case "repeater":
      const items = Array.isArray(value) ? value : [];
      return (
        <div className="space-y-4">
          <div className="flex items-center justify-between border-b border-gray-200 pb-2">
            <span className="text-md font-semibold text-gray-800">
              {label} {required && <span className="text-red-500">*</span>}
            </span>
          </div>

          {items.length === 0 ? (
            <div className="text-center py-6 border-2 border-dashed border-gray-200 rounded-xl bg-gray-50">
              <span className="text-sm text-gray-500">No items added yet.</span>
            </div>
          ) : (
            <div className="space-y-6">
              {items.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="relative p-5 bg-white border border-gray-200 rounded-xl shadow-sm space-y-4"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                      Entry #{index + 1}
                    </span>
                    {!disabled && (
                      <button
                        type="button"
                        onClick={() => handleRepeaterRemove(index)}
                        className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Remove row"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {field.fields.map((subField: any) => {
                      const subError = getSubError(`${index}.${subField.id}`);

                      let resolvedSubField = subField;
                      if (
                        field.id === "qualificationsList" &&
                        subField.id === "type" &&
                        subField.type === "select" &&
                        subField.options
                      ) {
                        const selectedInOtherRows = items
                          .filter((_, idx) => idx !== index)
                          .map((itm: any) => itm.type)
                          .filter(Boolean);

                        const filteredOptions = subField.options.filter(
                          (opt: any) => !selectedInOtherRows.includes(opt)
                        );

                        resolvedSubField = {
                          ...subField,
                          options: filteredOptions,
                        };
                      }

                      return (
                        <React.Fragment key={subField.id}>
                          <DynamicField
                            field={resolvedSubField}
                            value={item[subField.id]}
                            onChange={(subId: string, subVal: any) => handleRepeaterChange(index, subId, subVal)}
                            error={subError}
                            applicationId={applicationId}
                            disabled={disabled}
                            folder={folder}
                          />
                        </React.Fragment>
                      );
                    })}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
          {!disabled && (
            <div className="flex items-center justify-end pt-3">
              <button
                type="button"
                onClick={handleRepeaterAdd}
                className="inline-flex items-center justify-center gap-1.5 px-4 py-2 text-sm font-semibold text-blue-600 bg-blue-50 hover:bg-blue-100 hover:text-blue-700 active:bg-blue-200 rounded-xl border border-blue-200 transition-all duration-200 shadow-sm hover:shadow active:scale-95 cursor-pointer"
              >
                <Plus className="w-4 h-4" strokeWidth={2.5} /> Add Entry
              </button>
            </div>
          )}
          {error && typeof error === "string" && (
            <p className="text-xs text-red-500 flex items-center gap-1">
              <AlertCircle className="w-3.5 h-3.5" /> {error}
            </p>
          )}
        </div>
      );

    default:
      return null;
  }
}
